import React from 'react';
import { LucideIcon } from 'lucide-react';

interface Option {
  id: string;
  name: string;
}

interface FilterInputProps {
  label: string;
  icon: LucideIcon;
  type: 'select' | 'date' | 'radio' | 'text' | 'number';
  value: string | boolean | number;
  onChange: (value: string | boolean | number) => void;
  options?: Option[];
  placeholder?: string;
  radioOptions?: Array<{ label: string; value: boolean }>;
  className?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
}

const FilterInput: React.FC<FilterInputProps> = ({
  label,
  icon: Icon,
  type,
  value,
  onChange,
  options = [],
  placeholder = 'Seleccionar',
  radioOptions = [
    { label: 'Sí', value: true },
    { label: 'No', value: false },
  ],
  className = '',
  min = 1,
  step = 1,
  disabled = false,
}) => {
  const base =
    'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent';

  const cls = disabled
    ? `${base} bg-gray-100 opacity-70 cursor-not-allowed`
    : base;
  const renderInput = (): React.ReactNode => {
    switch (type) {
      case 'select':
        return (
          <select
            value={String(value)}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            className={cls}
            disabled={disabled}
          >
            <option value="">{placeholder}</option>
            {options.map((option) => (
              // {<option key={option.id} value={option.name}>}
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={String(value)}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            className={cls}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            min={min}
            step={step}
            value={value === '' ? '' : Number(value)}
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange(Number.isNaN(n) ? '' : n);
            }}
            className={cls}
            disabled={disabled}
          />
        );

      case 'text':
        return (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            placeholder={placeholder}
            className={cls}
            disabled={disabled}
          />
        );

      case 'radio':
        return (
          <div
            className={`flex items-center gap-6 ${
              disabled ? 'opacity-70' : ''
            }`}
          >
            {radioOptions.map((opt) => (
              <label key={opt.label} className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name={label.toLowerCase().replace(/\s+/g, '-')}
                  className="accent-primary-dark"
                  checked={value === opt.value}
                  onChange={() => {
                    onChange(opt.value);
                  }}
                  disabled={disabled}
                />
                <span className="text-sm text-gray-700">{opt.label}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={className}>
      <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
        <Icon className="w-4 h-4 mr-2" />
        {label}
      </label>
      {renderInput()}
    </div>
  );
};

export default FilterInput;
